import React, { useEffect, useMemo, useState } from 'react';
import { Container, Tooltip } from 'components';
import styles from './safetyEvents.module.scss';
import { ResponsiveLine } from '@nivo/line';
import { ResponsivePie } from '@nivo/pie';
import { ResponsiveBar, BarDatum } from '@nivo/bar';
import { Box, Grid, Paper, FormControl, InputLabel, Select, MenuItem, SelectChangeEvent, Alert, Snackbar, Typography, CircularProgress, IconButton, Tooltip as MuiTooltip, Autocomplete, TextField } from '@mui/material';
import {
  getEventTrends,
  getEventTypeDistribution,
  getEventsByTimeOfDay,
  getSafetyKPIs
} from 'api';
import {
  EventTrendItem,
  EventDistributionItem,
  EventsByTimeOfDayItem,
  SafetyKPIs,
  EventsByLocationItem
} from 'api/carrierSafetyEvents';
import { getCarrierSafetyEventsTypes, getVehiclesSafetyEventsList } from 'api/claimsSearching';
import { formatEventTypeLabel } from 'tools/helper';
import { reportException } from 'tools/logs';
import RefreshIcon from '@mui/icons-material/Refresh';
import { APIProvider } from '@vis.gl/react-google-maps';
import LocationChart from './locationChart';
import { isCarrierLogin } from 'tools/auth';

interface ISafetyEventsProps {
  print?: boolean;
  useCarrierFilters?: boolean;
}

// Define interface that satisfies BarDatum for the time of day chart
interface TimeOfDayBarDatum extends BarDatum {
  hour: string;
  count: number;
}

interface EventTypeItem {
  id: number;
  eventType: string;
  type: string;
}

// Define a more flexible type for the tooltip component
interface CustomTooltipProps {
  point: any; // Using any here as Nivo's Point type is complex
  eventTrend: EventTrendItem[];
}

// Update the CustomTooltip component to use the new data structure
const CustomTooltip = ({ point, eventTrend }: CustomTooltipProps) => {
  const pointData = eventTrend[parseInt(point.data.x.split(' ')[1]) - 1];
  
  if (!pointData) return null;
  
  return (
    <div
      style={{
        background: 'white',
        padding: '9px 12px',
        border: '1px solid #ccc',
        borderRadius: '4px',
        boxShadow: '0 2px 5px rgba(0,0,0,0.15)',
      }}
    >
      <div style={{ marginBottom: '5px', fontWeight: 'bold' }}>
        {pointData.date}
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '3px' }}>
        <span style={{ marginRight: '10px', color: '#36a8ee', fontWeight: 'bold' }}>Total Events:</span>
        <span>{pointData.totalEvents}</span>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <span style={{ marginRight: '10px', color: '#ff9800', fontWeight: 'bold' }}>Events per 1k Miles:</span>
        <span>{pointData.eventsPer1kMiles.toFixed(2)}</span>
      </div>
    </div>
  );
};

const SafetyEventsDashboard: React.FC<ISafetyEventsProps> = (props) => {
  const { print = false, useCarrierFilters = true } = props;
  const [timeframe, setTimeframe] = useState('30');
  const [eventTypeList, setEventTypeList] = useState<EventTypeItem[]>([]);
  const [eventType, setEventType] = useState('all');
  const [selectedEventTypes, setSelectedEventTypes] = useState<string[]>([]);
  const carrierId : any = isCarrierLogin() ? localStorage.getItem('companyId') : localStorage.getItem('carrierId') || '1'; // Default to 1 if not available
  const [hasData, setHasData] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  
  // Add state for initial loading
  const [isLoadingEventTypes, setIsLoadingEventTypes] = useState(true);

  // Replace single loading state with individual loading states
  const [isLoadingMetrics, setIsLoadingMetrics] = useState(false);
  const [isLoadingTrends, setIsLoadingTrends] = useState(false);
  const [isLoadingDistribution, setIsLoadingDistribution] = useState(false);
  const [isLoadingTimeOfDay, setIsLoadingTimeOfDay] = useState(false);
  const [isLoadingEventsByLocation, setIsLoadingEventsByLocation] = useState(false);

  // Add individual error states
  const [metricsError, setMetricsError] = useState<string | null>(null);
  const [trendsError, setTrendsError] = useState<string | null>(null);
  const [distributionError, setDistributionError] = useState<string | null>(null);
  const [timeOfDayError, setTimeOfDayError] = useState<string | null>(null);
  const [eventsByLocationError, setEventsByLocationError] = useState<string | null>(null);

  // Add individual data availability states
  const [hasMetricsData, setHasMetricsData] = useState(false);
  const [hasTrendsData, setHasTrendsData] = useState(false);
  const [hasDistributionData, setHasDistributionData] = useState(false);
  const [hasTimeOfDayData, setHasTimeOfDayData] = useState(false);
  const [hasEventsByLocationData, setHasEventsByLocationData] = useState(false);
  // Add main error state
  const [error, setError] = useState<string | null>(null);
  const [showErrorToast, setShowErrorToast] = useState(false);

  // State for filtered data with proper typing
  const [trendData, setTrendData] = useState<EventTrendItem[]>([]);
  const [distributionData, setDistributionData] = useState<EventDistributionItem[]>([]);
  const [eventsByTimeOfDayData, setEventsByTimeOfDayData] = useState<EventsByTimeOfDayItem[]>([]);
  const [eventsByLocationData, setEventsByLocationData] = useState<EventsByLocationItem[]>([])
  const [metrics, setMetrics] = useState<SafetyKPIs>({
    totalEvents: 0,
    eventsPer1kMiles: {
      fleetAverage: 0,
      industryAverage: 0
    },
    mostCommonEvent: {
      eventType: '',
      percentage: 0
    },
    harshEventScore: {
      score: 0,
      industryAverage: 0
    }
  });

  const sortedSafetyEventsTypes = useMemo(() => {
    const allEventsOption = {
      id: 0,
      eventType: 'All Events',
      type: 'all',
      firstLetter: 'A'
    }
    return [allEventsOption, ...eventTypeList]
        .map((item) => {
            const firstLetter = item?.eventType[0].toUpperCase()
            return {
                ...item,
                firstLetter: /[0-9]/.test(firstLetter) ? '0-9' : firstLetter,
            }
        })
        .sort((a, b) => a.eventType?.localeCompare(b?.eventType))
}, [eventTypeList])

  useEffect(() => {
    setIsLoadingEventTypes(true);
    getCarrierSafetyEventsTypes(carrierId)
      .then((res: any) => {
        if (res?.listSELabel) {
          const formattedList = res.listSELabel.map(
            (item: { id: number; eventType: string }) => ({
              id: item?.id,
              eventType: formatEventTypeLabel(item?.eventType),
              type: item?.eventType,
            })
          )
          setEventType('all')
          setEventTypeList(formattedList)
          // Initialize selectedEventTypes with all event types formatted correctly
          setSelectedEventTypes(formattedList.map((item: { id: number; eventType: string; type: string }) => 
            formatEventTypeLabel(item.eventType).replaceAll(" ", "_").toUpperCase()))
        }
        // if (res?.hasSafetyEvents) {
        //   setHasSafetyEvents(res.hasSafetyEvents)
        // }
      })
      .finally(() => setIsLoadingEventTypes(false))
  }, [])

  // Apply filters when timeframe or eventType changes
  useEffect(() => {
    // Calculate date range based on timeframe
    const endDate = new Date();
    endDate.setHours(23, 59, 59, 0);
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - parseInt(timeframe));
    startDate.setHours(0, 0, 0, 0);

    // Format dates to YYYY-MM-DDThh:mm:ss
    const formatDate = (date: Date, isEndDate: boolean = false): string => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const time = isEndDate ? '23:59:59' : '00:00:00';
      return `${year}-${month}-${day}T${time}`;
    };

    const startDateString = formatDate(startDate);
    const endDateString = formatDate(endDate, true);

    if (selectedEventTypes.length > 0) {
      // Load metrics data
      const loadMetricsData = async () => {
        setIsLoadingMetrics(true);
        setMetricsError(null);
        try {
          const metricsResponse = await getSafetyKPIs(carrierId, selectedEventTypes, startDateString, endDateString);
          if (metricsResponse && metricsResponse.totalEvents && metricsResponse.totalEvents > 0) {
            setMetrics(metricsResponse);
            setHasMetricsData(true);
          } else {
            setHasMetricsData(false);
          }
        } catch (err) {
          const errorMessage = err instanceof Error ? err.message : 'Failed to load safety metrics';
          setMetricsError(errorMessage);
          setHasMetricsData(false);
          reportException(err);
        } finally {
          setIsLoadingMetrics(false);
        }
      };

      // Load trends data
      const loadTrendsData = async () => {
        setIsLoadingTrends(true);
        setTrendsError(null);
        try {
          const trendsResponse = await getEventTrends(carrierId, selectedEventTypes, startDateString, endDateString);
          if (trendsResponse && trendsResponse.dailyTrends && trendsResponse.dailyTrends.length > 0) {
            setTrendData(trendsResponse.dailyTrends);
            setHasTrendsData(true);
          } else {
            setHasTrendsData(false);
          }
        } catch (err) {
          const errorMessage = err instanceof Error ? err.message : 'Failed to load trends data';
          setTrendsError(errorMessage);
          setHasTrendsData(false);
          reportException(err);
        } finally {
          setIsLoadingTrends(false);
        }
      };

      // Load distribution data
      const loadDistributionData = async () => {
        setIsLoadingDistribution(true);
        setDistributionError(null);
        try {
          const distributionResponse = await getEventTypeDistribution(carrierId, selectedEventTypes, startDateString, endDateString);
          if (distributionResponse && distributionResponse.distributions && distributionResponse.distributions.length > 0) {
            setDistributionData(distributionResponse.distributions);
            setHasDistributionData(true);
          } else {
            setHasDistributionData(false);
          }
        } catch (err) {
          const errorMessage = err instanceof Error ? err.message : 'Failed to load distribution data';
          setDistributionError(errorMessage);
          setHasDistributionData(false);
          reportException(err);
        } finally {
          setIsLoadingDistribution(false);
        }
      };

      // Load time of day data
      const loadTimeOfDayData = async () => {
        setIsLoadingTimeOfDay(true);
        setTimeOfDayError(null);
        try {
          const timeOfDayResponse = await getEventsByTimeOfDay(carrierId, selectedEventTypes, startDateString, endDateString);
          if (timeOfDayResponse && timeOfDayResponse.eventsByHour && timeOfDayResponse.eventsByHour.length > 0) {
            setEventsByTimeOfDayData(timeOfDayResponse.eventsByHour);
            setHasTimeOfDayData(true);
          } else {
            setHasTimeOfDayData(false);
          }
        } catch (err) {
          const errorMessage = err instanceof Error ? err.message : 'Failed to load time of day data';
          setTimeOfDayError(errorMessage);
          setHasTimeOfDayData(false);
          reportException(err);
        } finally {
          setIsLoadingTimeOfDay(false);
        }
      };

      const loadEventsByLocationData = async () => {
        setIsLoadingEventsByLocation(true);
        setEventsByLocationError(null);
        try {
          const payload = {
            startDate: startDateString,
            endDate: endDateString,
            types: selectedEventTypes ?? [],
            limit: 20000
          }
          const eventsByLocationResponse = await getVehiclesSafetyEventsList(carrierId, payload);
          if (eventsByLocationResponse && eventsByLocationResponse?.safetyEvents && eventsByLocationResponse.safetyEvents.length > 0) {
            setEventsByLocationData(eventsByLocationResponse.safetyEvents);
            setHasEventsByLocationData(true);
          } else {
            setHasEventsByLocationData(false);
          }
        } catch (err) {
          const errorMessage = err instanceof Error ? err.message : 'Failed to load events by location data';
          setEventsByLocationError(errorMessage);
          setHasEventsByLocationData(false);
          reportException(err);
        } finally {
          setIsLoadingEventsByLocation(false);
        }
      }

      // Execute all data loading functions independently
      loadMetricsData();
      loadTrendsData();
      loadDistributionData();
      loadTimeOfDayData();
      loadEventsByLocationData();
    }
  }, [timeframe, eventType, carrierId, selectedEventTypes]);

  const handleTimeframeChange = (event: SelectChangeEvent) => {
    setTimeframe(event.target.value as string);
  };

  const handleEventTypeChange = (_event: any, newValue: EventTypeItem | null) => {
    if (newValue) {
      const eventValue = newValue.type;
      setEventType(eventValue);
      
      if (eventValue === 'all') {
        setSelectedEventTypes(eventTypeList.map((item: EventTypeItem) => 
          item.eventType.replaceAll(" ", "_").toUpperCase()
        ));
      } else {
        const selectedItem = eventTypeList.find((item: EventTypeItem) => item.type === eventValue);
        setSelectedEventTypes(selectedItem ? [selectedItem.eventType.replaceAll(" ", "_").toUpperCase()] : []);
      }
    }
  };

  const handleCloseErrorToast = () => {
    setShowErrorToast(false);
  };

  const handleRefresh = () => {
    // Calculate date range based on timeframe
    const endDate = new Date();
    //endDate.setHours(23, 59, 59, 0);
    endDate.setHours(0, 0, 0, 0);
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - parseInt(timeframe));
    startDate.setHours(0, 0, 0, 0);

    // Format dates to YYYY-MM-DDThh:mm:ss
    const formatDate = (date: Date, isEndDate: boolean = false): string => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const time = isEndDate ? '23:59:59' : '00:00:00';
      return `${year}-${month}-${day}T${time}`;
    };

    const startDateString = formatDate(startDate);
    const endDateString = formatDate(endDate, true);

    if (selectedEventTypes.length > 0) {
      // Load metrics data
      const loadMetricsData = async () => {
        setIsLoadingMetrics(true);
        setMetricsError(null);
        try {
          const metricsResponse = await getSafetyKPIs(carrierId, selectedEventTypes, startDateString, endDateString);
          if (metricsResponse && metricsResponse.totalEvents && metricsResponse.totalEvents > 0) {
            setMetrics(metricsResponse);
            setHasMetricsData(true);
          } else {
            setHasMetricsData(false);
          }
        } catch (err) {
          const errorMessage = err instanceof Error ? err.message : 'Failed to load safety metrics';
          setMetricsError(errorMessage);
          setHasMetricsData(false);
          reportException(err);
        } finally {
          setIsLoadingMetrics(false);
        }
      };

      // Load trends data
      const loadTrendsData = async () => {
        setIsLoadingTrends(true);
        setTrendsError(null);
        try {
          const trendsResponse = await getEventTrends(carrierId, selectedEventTypes, startDateString, endDateString);
          if (trendsResponse && trendsResponse.dailyTrends && trendsResponse.dailyTrends.length > 0) {
            setTrendData(trendsResponse.dailyTrends);
            setHasTrendsData(true);
          } else {
            setHasTrendsData(false);
          }
        } catch (err) {
          const errorMessage = err instanceof Error ? err.message : 'Failed to load trends data';
          setTrendsError(errorMessage);
          setHasTrendsData(false);
          reportException(err);
        } finally {
          setIsLoadingTrends(false);
        }
      };

      // Load distribution data
      const loadDistributionData = async () => {
        setIsLoadingDistribution(true);
        setDistributionError(null);
        try {
          const distributionResponse = await getEventTypeDistribution(carrierId, selectedEventTypes, startDateString, endDateString);
          if (distributionResponse && distributionResponse.distributions && distributionResponse.distributions.length > 0) {
            setDistributionData(distributionResponse.distributions);
            setHasDistributionData(true);
          } else {
            setHasDistributionData(false);
          }
        } catch (err) {
          const errorMessage = err instanceof Error ? err.message : 'Failed to load distribution data';
          setDistributionError(errorMessage);
          setHasDistributionData(false);
          reportException(err);
        } finally {
          setIsLoadingDistribution(false);
        }
      };

      // Load time of day data
      const loadTimeOfDayData = async () => {
        setIsLoadingTimeOfDay(true);
        setTimeOfDayError(null);
        try {
          const timeOfDayResponse = await getEventsByTimeOfDay(carrierId, selectedEventTypes, startDateString, endDateString);
          if (timeOfDayResponse && timeOfDayResponse.eventsByHour && timeOfDayResponse.eventsByHour.length > 0) {
            setEventsByTimeOfDayData(timeOfDayResponse.eventsByHour);
            setHasTimeOfDayData(true);
          } else {
            setHasTimeOfDayData(false);
          }
        } catch (err) {
          const errorMessage = err instanceof Error ? err.message : 'Failed to load time of day data';
          setTimeOfDayError(errorMessage);
          setHasTimeOfDayData(false);
          reportException(err);
        } finally {
          setIsLoadingTimeOfDay(false);
        }
      };

      const loadEventsByLocationData = async () => {
        setIsLoadingEventsByLocation(true);
        setEventsByLocationError(null);
        try {
          const payload = {
            startDate: startDateString,
            endDate: endDateString,
            types: selectedEventTypes ?? [],
            limit: 20000
          }
          const eventsByLocationResponse = await getVehiclesSafetyEventsList(carrierId, payload);
          if (eventsByLocationResponse && eventsByLocationResponse?.safetyEvents && eventsByLocationResponse.safetyEvents.length > 0) {
            setEventsByLocationData(eventsByLocationResponse.safetyEvents);
            setHasEventsByLocationData(true);
          } else {
            setHasEventsByLocationData(false);
          }
        } catch (err) {
          const errorMessage = err instanceof Error ? err.message : 'Failed to load events by location data';
          setEventsByLocationError(errorMessage);
          setHasEventsByLocationData(false);
          reportException(err);
        } finally {
          setIsLoadingEventsByLocation(false);
        }
      }

      // Execute all data loading functions independently
      loadMetricsData();
      loadTrendsData();
      loadDistributionData();
      loadTimeOfDayData();
      loadEventsByLocationData();
    }
  };

  return (
    <Container className={styles.safetyEventsContainer}>
      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Error Toast */}
      <Snackbar
        open={showErrorToast}
        autoHideDuration={6000}
        onClose={handleCloseErrorToast}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseErrorToast} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>

      {/* Header with Title and Filters */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 280 }}>
            <Autocomplete
              id="SafetyEventsTypesSelect"
              options={sortedSafetyEventsTypes}
              value={sortedSafetyEventsTypes.find(item => item.type === eventType) || sortedSafetyEventsTypes[0]}
              onChange={handleEventTypeChange}
              getOptionLabel={(option) => option.eventType}
              groupBy={(option) => option?.firstLetter}
              disableClearable
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Event Type"
                  size="small"
                />
              )}
              isOptionEqualToValue={(option, value) => option.type === value.type}
            />
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel id="timeframe-select-label">Timeframe</InputLabel>
            <Select
              labelId="timeframe-select-label"
              id="timeframe-select"
              value={timeframe}
              label="Timeframe"
              onChange={handleTimeframeChange}
            >
              <MenuItem value="30" id="se-timeframe-30">Last 30 Days</MenuItem>
              <MenuItem value="90" id="se-timeframe-90">Last 90 Days</MenuItem>
              <MenuItem value="365" id="se-timeframe-365">Last 365 Days</MenuItem>
            </Select>
          </FormControl>
          <Tooltip title="Refresh data" placement="top">
            <IconButton 
              onClick={handleRefresh} 
              color="primary" 
              disabled={isLoadingMetrics || isLoadingTrends || isLoadingDistribution || isLoadingTimeOfDay || isLoadingEventsByLocation}
              aria-label="refresh data"
              id="refresh-safety-data-btn"
              size="small"
              sx={{ 
                ml: 1,
                bgcolor: 'rgba(0, 136, 225, 0.1)',
                '&:hover': { bgcolor: 'rgba(0, 136, 225, 0.2)' },
              }}
            >
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Initial Loading Screen */}
      {isLoadingEventTypes ? (
        <Paper 
          elevation={0} 
          sx={{ 
            p: 4, 
            border: '1px solid #E9E9E9', 
            borderRadius: '14px', 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            justifyContent: 'center',
            minHeight: '400px',
            textAlign: 'center'
          }}
        >
          <CircularProgress size={50} sx={{ mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            Loading Safety Event Data...
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Please wait while we fetch the initial data
          </Typography>
        </Paper>
      ) : (
        <>
          {/* Metrics Cards Section */}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={3}>
              <Paper elevation={0} sx={{ p: 2, border: '1px solid #E9E9E9', borderRadius: '14px', height: '160px', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', textAlign: 'center' }} id="se-metric-total-events">
                <div className={styles.metricLabel}>Total Events</div>
                {isLoadingMetrics ? (
                  <CircularProgress size={30} sx={{ my: 2 }} />
                ) : hasMetricsData && metrics.totalEvents ? (
                  <>
                    <div className={styles.metricValue} style={{ color: '#0088E1', fontSize: '2.5rem', fontWeight: 600 }} id="se-metric-total-events-value">
                      {metrics.totalEvents}
                    </div>
                    <div className={styles.metricSubtitle}>Last {timeframe} days</div>
                  </>
                ) : (
                  <div className={styles.metricSubtitle} style={{ color: '#0088E1', fontSize: '1.5rem' }}>
                    No data available
                  </div>
                )}
              </Paper>
            </Grid>
            <Grid item xs={3}>
              <Paper elevation={0} sx={{ p: 2, border: '1px solid #E9E9E9', borderRadius: '14px', height: '160px', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', textAlign: 'center' }} id="se-metric-events-per-mile">
                <div className={styles.metricLabel}>Events per 1k Miles</div>
                {isLoadingMetrics ? (
                  <CircularProgress size={30} sx={{ my: 2 }} />
                ) : hasMetricsData && metrics.eventsPer1kMiles?.fleetAverage ? (
                  <>
                    <Tooltip title={metrics.eventsPer1kMiles.fleetAverage.toString()} placement="top">
                      <div className={styles.metricValue} style={{ color: '#0088E1', fontSize: '2.5rem', fontWeight: 600 }} id="se-metric-events-per-mile-value">
                        {metrics.eventsPer1kMiles.fleetAverage.toFixed(2)}
                      </div>
                    </Tooltip>
                    <div className={styles.metricSubtitle}>Fleet average</div>
                    <Tooltip title={metrics.eventsPer1kMiles.industryAverage.toString()} placement="bottom">
                      <div className={styles.metricNote}>Industry average: {metrics.eventsPer1kMiles.industryAverage.toFixed(2)}</div>
                    </Tooltip>
                  </>
                ) : (
                  <div className={styles.metricSubtitle} style={{ color: '#0088E1', fontSize: '1.5rem' }}>
                    No data available
                  </div>
                )}
              </Paper>
            </Grid>
            <Grid item xs={3}>
              <Paper elevation={0} sx={{ p: 2, border: '1px solid #E9E9E9', borderRadius: '14px', height: '160px', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', textAlign: 'center' }} id="se-metric-most-common">
                <div className={styles.metricLabel}>Most Common Event</div>
                {isLoadingMetrics ? (
                  <CircularProgress size={30} sx={{ my: 2 }} />
                ) : hasMetricsData && metrics.mostCommonEvent?.eventType ? (
                  <>
                    <div className={styles.metricValue} style={{ color: '#FF8A00', fontSize: '2.5rem', fontWeight: 600 }} id="se-metric-most-common-value">
                      {formatEventTypeLabel(metrics.mostCommonEvent.eventType)}
                    </div>
                    <div className={styles.metricSubtitle}>{metrics.mostCommonEvent.percentage}% of events</div>
                  </>
                ) : (
                  <div className={styles.metricSubtitle} style={{ color: '#FF8A00', fontSize: '1.5rem' }}>
                    No data available
                  </div>
                )}
              </Paper>
            </Grid>
            <Grid item xs={3}>
              <Paper elevation={0} sx={{ p: 2, border: '1px solid #E9E9E9', borderRadius: '14px', height: '160px', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', textAlign: 'center' }} id="se-metric-harsh-events">
                <div className={styles.metricLabel}>Harsh Events Score</div>
                {isLoadingMetrics ? (
                  <CircularProgress size={30} sx={{ my: 2 }} />
                ) : hasMetricsData && metrics.harshEventScore?.score ? (
                  <>
                    <Tooltip title={metrics.harshEventScore.score.toString()} placement="top">
                      <div className={styles.metricValue} style={{ fontSize: '2.5rem', fontWeight: 600 }} id="se-metric-harsh-events-value">
                        {metrics.harshEventScore.score.toFixed(2)}
                      </div>
                    </Tooltip>
                    <div className={styles.metricSubtitle}>Out of 100</div>
                    <Tooltip title={metrics.harshEventScore.industryAverage.toString()} placement="bottom">
                      <div className={styles.metricNote}>Industry average: {metrics.harshEventScore.industryAverage.toFixed(2)}</div>
                    </Tooltip>
                  </>
                ) : (
                  <div className={styles.metricSubtitle} style={{ fontSize: '1.5rem' }}>
                    No data available
                  </div>
                )}
              </Paper>
            </Grid>
          </Grid>

          {/* Charts Grid - Only show when initial data loading is complete */}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            {/* Safety Events Trends */}
            <Grid item xs={6}>
              <Paper elevation={0} sx={{ p: 2, border: '1px solid #E9E9E9', borderRadius: '14px', height: '400px' }} id="se-chart-trends" className={styles.largerLegends}>
                <div className={styles.chartTitle}>Safety Events Trends</div>
                <div style={{ height: 320, position: 'relative' }}>
                  {isLoadingTrends ? (
                    <Box 
                      sx={{ 
                        display: 'flex', 
                        flexDirection: 'column', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        height: '100%',
                        textAlign: 'center'
                      }}
                    >
                      <CircularProgress size={40} sx={{ mb: 2 }} />
                      <Typography variant="body2" color="text.secondary">
                        Loading trends data...
                      </Typography>
                    </Box>
                  ) : hasTrendsData && trendData.length > 0 ? (
                    <>
                      {/* Shared constants for both charts to ensure alignment */}
                      <div style={{ position: 'absolute', top: 0, left: 10, width: 'calc(100% - 20px)', height: '100%' }}>
                        <ResponsiveLine
                          data={[
                            {
                              id: 'Total Events',
                              color: '#36a8ee',
                              data: trendData.map((item, index) => ({
                                x: `Week ${index + 1}`,
                                y: item.totalEvents,
                                originalDate: item.date
                              }))
                            }
                          ]}
                          margin={{ top: 40, right: 60, bottom: 80, left: 70 }}
                          xScale={{ type: 'point' }}
                          yScale={{
                            type: 'linear',
                            min: 'auto',
                            max: 'auto',
                            stacked: false
                          }}
                          curve="monotoneX"
                          axisTop={null}
                          axisRight={null}
                          axisBottom={{
                            tickSize: 5,
                            tickPadding: 5,
                            tickRotation: -45,
                            legendOffset: 60,
                            legendPosition: 'middle',
                            renderTick: ({ value, x, y }) => {
                              // Extract week number and validate index
                              const weekNum = parseInt(value.split(' ')[1]) - 1;
                              const dateRange = weekNum >= 0 && weekNum < trendData.length ? trendData[weekNum].date : '';
                              
                              return (
                                <g transform={`translate(${x},${y})`}>
                                  <text
                                    x={-75}
                                    y={12}
                                    transform="rotate(-45)"
                                    textAnchor="start"
                                    style={{ fill: '#777', fontSize: 11 }}
                                  >
                                    {value}
                                  </text>
                                  {dateRange && <title>{dateRange}</title>}
                                </g>
                              );
                            }
                          }}
                          axisLeft={{
                            tickSize: 5,
                            tickPadding: 5,
                            tickRotation: 0,
                            legend: 'Total Events',
                            legendOffset: -60,
                            legendPosition: 'middle'
                          }}
                          colors={['#36a8ee']}
                          lineWidth={3}
                          pointSize={8}
                          pointColor={{ theme: 'background' }}
                          pointBorderWidth={2}
                          pointBorderColor={{ from: 'serieColor' }}
                          enablePointLabel={false}
                          useMesh={true}
                          tooltip={({ point }) => (
                            <CustomTooltip point={point} eventTrend={trendData} />
                          )}
                          legends={[]}
                          theme={{
                            grid: {
                              line: {
                                stroke: '#e0e0e0',
                                strokeWidth: 1
                              }
                            },
                            axis: {
                              legend: {
                                text: {
                                  fontSize: 11,
                                  fill: '#777'
                                }
                              },
                              ticks: {
                                text: {
                                  fontSize: 11,
                                  fill: '#777'
                                }
                              }
                            },
                            tooltip: {
                              container: {
                                background: 'white',
                                fontSize: '12px',
                              }
                            }
                          }}
                          isInteractive={true}
                          animate={true}
                          motionConfig="gentle"
                          enableSlices={false}
                        />
                      </div>
                      
                      {/* Second chart with exact same dimensions and positioning */}
                      <div style={{ position: 'absolute', top: 0, left: 10, width: 'calc(100% - 20px)', height: '100%', pointerEvents: 'none' }}>
                        <ResponsiveLine
                          data={[
                            {
                              id: 'Events per 1k Miles',
                              color: '#ff9800',
                              data: trendData.map((item, index) => ({
                                x: `Week ${index + 1}`,
                                y: item.eventsPer1kMiles,
                                originalDate: item.date
                              }))
                            }
                          ]}
                          margin={{ top: 40, right: 60, bottom: 80, left: 70 }}
                          xScale={{ type: 'point' }}
                          yScale={{
                            type: 'linear',
                            min: 'auto',
                            max: 'auto',
                            stacked: false
                          }}
                          curve="monotoneX"
                          axisTop={null}
                          axisRight={{
                            tickSize: 5,
                            tickPadding: 5,
                            tickRotation: 0,
                            legend: 'Events per 1k Miles',
                            legendOffset: 50,
                            legendPosition: 'middle'
                          }}
                          axisBottom={null}
                          axisLeft={null}
                          enableGridX={false}
                          enableGridY={false}
                          colors={['#ff9800']}
                          lineWidth={3}
                          pointSize={8}
                          pointColor={{ theme: 'background' }}
                          pointBorderWidth={2}
                          pointBorderColor={{ from: 'serieColor' }}
                          enablePointLabel={false}
                          useMesh={false}
                          enableArea={false}
                          legends={[]}
                          isInteractive={false}
                          theme={{
                            axis: {
                              ticks: {
                                text: {
                                  fontSize: 9,
                                  fill: '#777'
                                }
                              }
                            }
                          }}
                          animate={true}
                          motionConfig="gentle"
                          enableSlices={false}
                        />
                      </div>
                      
                      {/* Custom legend above the chart */}
                      <div style={{ 
                        position: 'absolute', 
                        top: 0, 
                        left: 0, 
                        width: '100%', 
                        display: 'flex', 
                        justifyContent: 'center', 
                        padding: '5px',
                        zIndex: 10
                      }}>
                        <div style={{ display: 'flex', gap: '30px', backgroundColor: 'rgba(255,255,255,0.8)', padding: '5px 15px', borderRadius: '4px' }}>
                          <div style={{ display: 'flex', alignItems: 'center' }}>
                            <div style={{ 
                              width: '12px', 
                              height: '12px', 
                              backgroundColor: '#36a8ee', 
                              borderRadius: '50%', 
                              marginRight: '8px' 
                            }}></div>
                            <span>Total Events</span>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center' }}>
                            <div style={{ 
                              width: '12px', 
                              height: '12px', 
                              backgroundColor: '#ff9800', 
                              borderRadius: '50%', 
                              marginRight: '8px' 
                            }}></div>
                            <span>Events per 1k Miles</span>
                          </div>
                        </div>
                      </div>
                    </>
                  ) : (
                    <Box 
                      sx={{ 
                        display: 'flex', 
                        flexDirection: 'column', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        height: '100%',
                        textAlign: 'center'
                      }}
                    >
                      <Typography variant="body1" color="text.secondary">
                        No trend data available for the selected criteria
                      </Typography>
                    </Box>
                  )}
                </div>
              </Paper>
            </Grid>

            {/* Event Type Distribution */}
            <Grid item xs={6}>
              <Paper elevation={0} sx={{ p: 2, border: '1px solid #E9E9E9', borderRadius: '14px', height: '400px' }} id="se-chart-distribution" className={styles.largerLegends}>
                <div className={styles.chartTitle}>Event Type Distribution</div>
                <div style={{ height: 330 }}>
                  {isLoadingDistribution ? (
                    <Box 
                      sx={{ 
                        display: 'flex', 
                        flexDirection: 'column', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        height: '100%',
                        textAlign: 'center'
                      }}
                    >
                      <CircularProgress size={40} sx={{ mb: 2 }} />
                      <Typography variant="body2" color="text.secondary">
                        Loading distribution data...
                      </Typography>
                    </Box>
                  ) : hasDistributionData && distributionData.length > 0 ? (
                    <ResponsivePie
                      data={distributionData.map(item => ({
                        id: formatEventTypeLabel(item.eventType),
                        label: formatEventTypeLabel(item.eventType),
                        value: item.totalCount
                      }))}
                      margin={{ top: 20, right: 80, bottom: 80, left: 80 }}
                      innerRadius={0}
                      padAngle={0.7}
                      cornerRadius={3}
                      activeOuterRadiusOffset={8}
                      colors={['#FADE6B', '#F9706B', '#89EBB0', '#33A02C', '#FB9A99', '#E31A1C', '#FDBF6F', '#FF7F00', '#CAB2D6']}
                      borderWidth={1}
                      borderColor={{ from: 'color', modifiers: [['darker', 0.2]] }}
                      arcLabelsSkipAngle={10}
                      arcLabelsTextColor="#ffffff"
                      arcLinkLabelsSkipAngle={10}
                      arcLinkLabelsTextColor="#333333"
                      arcLinkLabelsThickness={2}
                      arcLinkLabelsColor={{ from: 'color' }}
                      arcLinkLabel="label"
                      arcLinkLabelsOffset={-10}
                      arcLinkLabelsDiagonalLength={16}
                      arcLinkLabelsStraightLength={24}
                      enableArcLabels={false}
                      enableArcLinkLabels={true}
                    />
                  ) : (
                    <Box 
                      sx={{ 
                        display: 'flex', 
                        flexDirection: 'column', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        height: '100%',
                        textAlign: 'center'
                      }}
                    >
                      <Typography variant="body1" color="text.secondary">
                        No distribution data available for the selected criteria
                      </Typography>
                    </Box>
                  )}
                </div>
              </Paper>
            </Grid>

            {/* Events by Time of Day */}
            <Grid item xs={6}>
              <Paper elevation={0} sx={{ p: 2, border: '1px solid #E9E9E9', borderRadius: '14px', height: '400px' }} id="se-chart-time-of-day" className={styles.largerLegends}>
                <div className={styles.chartTitle}>Events by Time of Day</div>
                <div style={{ height: 330 }}>
                  {isLoadingTimeOfDay ? (
                    <Box 
                      sx={{ 
                        display: 'flex', 
                        flexDirection: 'column', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        height: '100%',
                        textAlign: 'center'
                      }}
                    >
                      <CircularProgress size={40} sx={{ mb: 2 }} />
                      <Typography variant="body2" color="text.secondary">
                        Loading time of day data...
                      </Typography>
                    </Box>
                  ) : hasTimeOfDayData && eventsByTimeOfDayData.length > 0 ? (
                    <ResponsiveBar
                      data={eventsByTimeOfDayData as unknown as BarDatum[]}
                      keys={['count']}
                      indexBy="hour"
                      margin={{ top: 20, right: 20, bottom: 70, left: 60 }}
                      padding={0.1}
                      valueScale={{ type: 'linear' }}
                      indexScale={{ type: 'band', round: true }}
                      colors={({ data }) => {
                        const hour = data.hour as string;
                        const morningColor = '#0072BC';
                        const afternoonColor = '#00A8E1';
                        const eveningColor = '#FF8200';
                        const nightColor = '#333333';
                        
                        // Determine time of day based on hour string format - new format "12 AM"
                        if (hour.includes("12AM")) {
                          return nightColor; // 12 AM is part of the night period
                        } else if (hour.includes("12PM")) {
                          return afternoonColor; // 12 PM is part of the afternoon period
                        } else if (hour.includes("AM")) {
                          const hourNum = parseInt(hour.split(" ")[0]);
                          // Morning (5am-11am)
                          if (hourNum >= 5) {
                            return morningColor;
                          }
                          // Night (1am-4am)
                          else {
                            return nightColor;
                          }
                        } else if (hour.includes("PM")) {
                          const hourNum = parseInt(hour.split(" ")[0]);
                          // Afternoon (1pm-5pm)
                          if (hourNum <= 5) {
                            return afternoonColor;
                          }
                          // Evening (6pm-9pm)
                          else if (hourNum >= 6 && hourNum <= 9) {
                            return eveningColor;
                          }
                          // Night (10pm-11pm)
                          else if (hourNum >= 10) {
                            return nightColor;
                          }
                        }
                        return '#36a8ee'; // Default color
                      }}
                      borderColor={{ from: 'color', modifiers: [['darker', 0.6]] }}
                      axisTop={null}
                      axisRight={null}
                      axisBottom={{
                        tickSize: 5,
                        tickPadding: 5,
                        tickRotation: -45,
                        legend: 'Hour of Day',
                        legendPosition: 'middle',
                        legendOffset: 60
                      }}
                      axisLeft={{
                        tickSize: 5,
                        tickPadding: 5,
                        tickRotation: 0,
                        legend: 'Number of Events',
                        legendPosition: 'middle',
                        legendOffset: -50
                      }}
                      enableLabel={false}
                      animate={true}
                      motionConfig="gentle"
                      role="application"
                      ariaLabel="Events by hour of day"
                      legends={[
                        {
                          dataFrom: 'keys',
                          anchor: 'top',
                          direction: 'row',
                          justify: false,
                          translateX: 0,
                          translateY: -20,
                          itemsSpacing: 2,
                          itemWidth: 100,
                          itemHeight: 20,
                          itemDirection: 'left-to-right',
                          itemOpacity: 0.85,
                          symbolSize: 10,
                          symbolShape: 'square',
                          effects: [
                            {
                              on: 'hover',
                              style: {
                                itemOpacity: 1
                              }
                            }
                          ],
                          data: [
                            { id: 'Morning (5am-12pm)', label: 'Morning', color: '#0072BC' },
                            { id: 'Afternoon (12pm-5pm)', label: 'Afternoon', color: '#00A8E1' },
                            { id: 'Evening (5pm-10pm)', label: 'Evening', color: '#FF8200' },
                            { id: 'Night (10pm-5am)', label: 'Night', color: '#333333' }
                          ]
                        }
                      ]}
                    />
                  ) : (
                    <Box 
                      sx={{ 
                        display: 'flex', 
                        flexDirection: 'column', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        height: '100%',
                        textAlign: 'center'
                      }}
                    >
                      <Typography variant="body1" color="text.secondary">
                        No time of day data available for the selected criteria
                      </Typography>
                    </Box>
                  )}
                </div>
              </Paper>
            </Grid>

            {/* Events by Location */}
            <Grid item xs={6}>
              <Paper elevation={0} sx={{ p: 2, border: '1px solid #E9E9E9', borderRadius: '14px', height: '400px' }} id="se-chart-location">
                <div className={styles.chartTitle}>Events by Location</div>
                {isLoadingEventsByLocation ? (
                    <Box 
                    sx={{ 
                      display: 'flex', 
                      flexDirection: 'column', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      height: '100%',
                      textAlign: 'center'
                    }}
                  >
                    <CircularProgress size={40} sx={{ mb: 2 }} />
                    <Typography variant="body2" color="text.secondary">
                      Loading location data...
                    </Typography>
                  </Box>
                ) : hasEventsByLocationData && eventsByLocationData.length > 0 ? (
                  <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '330px',
                    backgroundColor: '#f5f5f5',
                    borderRadius: '8px',
                    color: '#666'
                  }}
                  id="se-chart-location-placeholder"
                >
                  <APIProvider
                    apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY || ''}
                  >
                    <LocationChart
                      safetyEventsList={eventsByLocationData}
                    />
                  </APIProvider>
                </Box>
                ) : (
                  <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '330px',
                    textAlign: 'center'
                  }}
                >
                  <Typography variant="body1" color="text.secondary">
                    No location data available for the selected criteria
                  </Typography>
                </Box>
                )}
              </Paper>
            </Grid>
          </Grid>
        </>
      )}
    </Container>
  );
};

export default SafetyEventsDashboard; 